import { FC, PointerEvent, useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@bonusly/design-system';

import { DOODLE_CANVAS_CURSOR } from '../../../../doodle-editor/doodle-cursor';
import { renderStrokeToCtx } from './stroke-utils';
import type { DoodleStroke } from './use-doodle';

interface DoodleCanvasProps {
	strokes: DoodleStroke[];
	currentStroke: DoodleStroke | null;
	onStartStroke: (point: number[]) => void;
	onAddPoint: (point: number[]) => void;
	onEndStroke: () => void;
}

export const DOODLE_CANVAS = { width: 800, height: 500 } as const;
export const DOODLE_EXPORT_SCALE = 2;

const getDisplayPixelRatio = (): number => {
	if (typeof window === 'undefined') return 1;
	return Math.max(1, Math.min(window.devicePixelRatio || 1, 3));
};

export const DoodleCanvas: FC<DoodleCanvasProps> = ({ strokes, currentStroke, onStartStroke, onAddPoint, onEndStroke }) => {
	const [pixelRatio] = useState(getDisplayPixelRatio);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const isDrawing = useRef(false);
	const rafRef = useRef<number | null>(null);
	const cacheCanvasRef = useRef<HTMLCanvasElement | null>(null);
	const cachedStrokesCountRef = useRef(0);

	const getCanvasPoint = useCallback((e: PointerEvent): number[] => {
		const canvas = canvasRef.current;
		if (!canvas) return [0, 0];

		const rect = canvas.getBoundingClientRect();
		const scaleX = DOODLE_CANVAS.width / rect.width;
		const scaleY = DOODLE_CANVAS.height / rect.height;

		const x = (e.clientX - rect.left) * scaleX;
		const y = (e.clientY - rect.top) * scaleY;
		const pressure = e.pointerType === 'mouse' || !e.pressure ? 0.5 : e.pressure;

		return [x, y, pressure];
	}, []);

	const handlePointerDown = useCallback(
		(e: PointerEvent) => {
			e.preventDefault();
			(e.target as Element).setPointerCapture(e.pointerId);
			isDrawing.current = true;
			onStartStroke(getCanvasPoint(e));
		},
		[getCanvasPoint, onStartStroke]
	);

	const handlePointerMove = useCallback(
		(e: PointerEvent) => {
			if (!isDrawing.current) return;
			e.preventDefault();

			if (rafRef.current !== null) return;
			rafRef.current = requestAnimationFrame(() => {
				rafRef.current = null;
				onAddPoint(getCanvasPoint(e));
			});
		},
		[getCanvasPoint, onAddPoint]
	);

	const handlePointerUp = useCallback(
		(e: PointerEvent) => {
			e.preventDefault();
			if (!isDrawing.current) return;
			isDrawing.current = false;
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			onAddPoint(getCanvasPoint(e));
			onEndStroke();
		},
		[getCanvasPoint, onAddPoint, onEndStroke]
	);

	useEffect(() => {
		if (!cacheCanvasRef.current) {
			cacheCanvasRef.current = document.createElement('canvas');
		}

		const cacheCanvas = cacheCanvasRef.current;
		const backingWidth = DOODLE_CANVAS.width * pixelRatio;
		const backingHeight = DOODLE_CANVAS.height * pixelRatio;
		if (cacheCanvas.width !== backingWidth || cacheCanvas.height !== backingHeight) {
			cacheCanvas.width = backingWidth;
			cacheCanvas.height = backingHeight;
			cachedStrokesCountRef.current = 0;
		}

		const cacheCtx = cacheCanvas.getContext('2d');
		if (!cacheCtx) return;
		cacheCtx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

		if (cachedStrokesCountRef.current > strokes.length || cachedStrokesCountRef.current === 0) {
			cacheCtx.fillStyle = '#ffffff';
			cacheCtx.fillRect(0, 0, DOODLE_CANVAS.width, DOODLE_CANVAS.height);
			for (const stroke of strokes) {
				renderStrokeToCtx(cacheCtx, stroke);
			}
			cachedStrokesCountRef.current = strokes.length;
		} else if (strokes.length > cachedStrokesCountRef.current) {
			for (let i = cachedStrokesCountRef.current; i < strokes.length; i++) {
				renderStrokeToCtx(cacheCtx, strokes[i]);
			}
			cachedStrokesCountRef.current = strokes.length;
		}
	}, [pixelRatio, strokes]);

	useEffect(() => {
		const canvas = canvasRef.current;
		const cacheCanvas = cacheCanvasRef.current;
		if (!canvas || !cacheCanvas) return;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		ctx.setTransform(1, 0, 0, 1, 0, 0);
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		ctx.imageSmoothingEnabled = true;
		ctx.imageSmoothingQuality = 'high';
		ctx.drawImage(cacheCanvas, 0, 0);

		if (currentStroke) {
			ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
			renderStrokeToCtx(ctx, currentStroke);
		}
	}, [strokes, currentStroke, pixelRatio]);

	return (
		<Box
			ref={containerRef}
			position="relative"
			width="100%"
			bg="white"
			overflow="hidden"
			sx={{ aspectRatio: `${DOODLE_CANVAS.width} / ${DOODLE_CANVAS.height}`, touchAction: 'none' }}
		>
			<canvas
				ref={canvasRef}
				width={DOODLE_CANVAS.width * pixelRatio}
				height={DOODLE_CANVAS.height * pixelRatio}
				style={{
					width: '100%',
					height: '100%',
					cursor: DOODLE_CANVAS_CURSOR,
					display: 'block',
				}}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={handlePointerUp}
				onPointerLeave={handlePointerUp}
			/>
		</Box>
	);
};
